import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation, AnimatePresence } from 'framer-motion';
import SeoJsonLd from '../../components/feature/SeoJsonLd';

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

function useScrollAnimation() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const controls = useAnimation();
  useEffect(() => {
    if (isInView) controls.start('visible');
  }, [isInView, controls]);
  return { ref, controls };
}

function ColumbusLogo({ className = 'w-12 h-12' }: { className?: string }) {
  return (
    <img
      src="https://static.readdy.ai/image/4d2bea2dc98a7e73f49e9d87ac7b145a/545924125de435e3ed3a074a7a9f96d5.png"
      alt="Logo Lavandería Colón"
      className={`${className} object-contain`}
    />
  );
}

/* ─── NAVBAR ─── */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  const links = [
    { label: 'Servicios', id: 'servicios' },
    { label: 'Reparto', id: 'reparto' },
    { label: 'Equipo', id: 'equipo' },
    { label: 'Contacto', id: 'contacto' },
  ];

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-white/95 backdrop-blur-xl shadow-sm border-b border-stone-100' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 lg:px-14 xl:px-24">
        <div className="flex items-center justify-between h-20">
          <button onClick={() => go('inicio')} className="flex items-center gap-3 cursor-pointer">
            <ColumbusLogo className="w-9 h-9" />
            <span className={`text-lg font-bold tracking-tight transition-colors ${scrolled ? 'text-stone-900' : 'text-white'}`}>
              Lavandería<span className="text-teal-400">Colón</span>
            </span>
          </button>

          <div className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled ? 'text-stone-600 hover:text-teal-600' : 'text-white/80 hover:text-white'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <button className="md:hidden cursor-pointer" onClick={() => setMobileOpen(!mobileOpen)}>
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl ${scrolled ? 'text-stone-900' : 'text-white'}`}></i>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-stone-100"
          >
            <div className="px-6 py-5 space-y-3">
              {links.map((l) => (
                <button key={l.id} onClick={() => go(l.id)} className="block w-full text-left py-2 text-stone-700 font-medium cursor-pointer">
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

/* ─── HERO ─── */
function HeroSection() {
  const go = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/e4c4d7ffe23e4b4aa1634a8c8c22eba4.jpg"
          alt="Lavandería profesional Barcelona"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/85 via-stone-900/70 to-stone-900/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/60 via-transparent to-transparent"></div>
      </div>

      {/* Floating badge */}
      <motion.div
        initial={{ opacity: 0, x: 60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.2, duration: 0.7 }}
        className="absolute top-32 right-10 lg:right-20 hidden lg:block"
      >
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse"></div>
            <span className="text-xs font-semibold tracking-widest uppercase text-teal-300">Disponible Ahora</span>
          </div>
          <p className="text-2xl font-bold">+30 años</p>
          <p className="text-sm text-white/60">de experiencia</p>
        </div>
      </motion.div>

      <div className="relative w-full px-6 lg:px-14 xl:px-24 pt-28 pb-20">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-8">
            <div className="h-px w-12 bg-teal-400"></div>
            <span className="text-teal-400 text-sm font-semibold tracking-widest uppercase">
              Calle Ample 9 · Barcelona
            </span>
          </motion.div>

          <motion.h1
            variants={fadeInUp}
            className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-white leading-[0.95] mb-8 tracking-tight"
          >
            Lavandería<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-emerald-400">
              Profesional
            </span><br />
            en Barcelona
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-lg text-white/65 mb-10 leading-relaxed max-w-xl">
            Especialistas en el cuidado de prendas para particulares, hoteles, restaurantes y centros médicos.
            Recogida y entrega gratuita en toda Barcelona.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 mb-12">
            <button
              onClick={() => go('contacto')}
              className="px-8 py-4 bg-teal-500 hover:bg-teal-400 text-white font-bold rounded-full transition-all shadow-lg shadow-teal-500/30 cursor-pointer whitespace-nowrap"
            >
              Contactar Ahora
            </button>
            <button
              onClick={() => go('servicios')}
              className="px-8 py-4 border border-white/30 text-white font-semibold rounded-full hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
            >
              Ver Servicios
            </button>
          </motion.div>

          <motion.div variants={fadeInUp} className="flex flex-wrap gap-6">
            {[
              { icon: 'ri-phone-fill', text: '620 550 559', href: 'tel:620550559' },
              { icon: 'ri-phone-fill', text: '933 020 486', href: 'tel:933020486' },
            ].map((c, i) => (
              <a key={i} href={c.href} className="flex items-center gap-2 text-white/70 hover:text-teal-300 transition-colors cursor-pointer">
                <i className={`${c.icon} text-teal-400`}></i>
                <span className="font-medium">{c.text}</span>
              </a>
            ))}
          </motion.div>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="absolute bottom-10 left-6 right-6 lg:left-14 xl:left-24 lg:right-14 xl:right-24"
        >
          <div className="bg-white/8 backdrop-blur-md border border-white/15 rounded-2xl px-8 py-5 grid grid-cols-2 md:grid-cols-3 gap-6">
            {[
              { num: '48h', label: 'Entrega en 48h' },
              { num: '100%', label: 'Barcelona cubierta' },
              { num: '30+', label: 'Años de experiencia' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl font-black text-white">{s.num}</p>
                <p className="text-xs text-white/50 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── SERVICES ─── */
function ServicesSection() {
  const { ref, controls } = useScrollAnimation();
  const [active, setActive] = useState(0);

  const services = [
    {
      icon: 'ri-hotel-line',
      tag: '01',
      title: 'Hoteles & Restauración',
      description: 'Servicio diario para hoteles, restaurantes y spas. Garantizamos entregas puntuales y calidad premium en cada prenda.',
      image: 'https://readdy.ai/api/search-image?query=luxury%20hotel%20bedroom%20with%20perfectly%20pressed%20white%20linen%20sheets%20and%20fluffy%20towels%2C%20elegant%20interior%20design%2C%20warm%20golden%20lighting%2C%20five%20star%20hospitality%2C%20crisp%20clean%20textiles%2C%20sophisticated%20atmosphere%2C%20professional%20photography%2C%20dark%20wood%20furniture%2C%20premium%20quality&width=500&height=600&seq=svc01&orientation=portrait',
      features: ['Ropa de cama', 'Toallas', 'Uniformes', 'Manteles'],
    },
    {
      icon: 'ri-building-4-line',
      tag: '02',
      title: 'Apartamentos Turísticos',
      description: 'Cambios rápidos entre huéspedes. Coordinación flexible según tus necesidades de check-in y check-out.',
      image: 'https://readdy.ai/api/search-image?query=modern%20Barcelona%20tourist%20apartment%20with%20fresh%20clean%20white%20linen%2C%20bright%20natural%20light%20through%20large%20windows%2C%20contemporary%20minimalist%20interior%2C%20Airbnb%20style%2C%20warm%20welcoming%20atmosphere%2C%20neatly%20folded%20towels%20on%20bed%2C%20stylish%20decor%2C%20professional%20real%20estate%20photography&width=500&height=600&seq=svc02&orientation=portrait',
      features: ['Cambios express', 'Sábanas', 'Toallas', 'Cortinas'],
    },
    {
      icon: 'ri-hospital-line',
      tag: '03',
      title: 'Centros Médicos',
      description: 'Tratamiento especializado para prendas sanitarias. Higienización certificada y procesos controlados.',
      image: 'https://readdy.ai/api/search-image?query=medical%20center%20clean%20white%20uniforms%20and%20sterile%20medical%20linens%20neatly%20stacked%2C%20clinical%20environment%2C%20bright%20white%20lighting%2C%20healthcare%20facility%2C%20organized%20and%20hygienic%2C%20professional%20medical%20photography%2C%20pristine%20condition%2C%20blue%20and%20white%20tones%2C%20modern%20hospital%20setting&width=500&height=600&seq=svc03&orientation=portrait',
      features: ['Batas médicas', 'Ropa quirúrgica', 'Sábanas', 'Higienización'],
    },
  ];

  return (
    <section id="servicios" className="py-28 lg:py-36 bg-stone-950 text-white overflow-hidden">
      <div className="w-full px-6 lg:px-14 xl:px-24">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16"
        >
          <div>
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-4">
              <div className="h-px w-10 bg-teal-500"></div>
              <span className="text-teal-400 text-xs font-bold tracking-widest uppercase">Nuestros Servicios</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
              Soluciones para<br />
              <span className="text-teal-400">cada sector</span>
            </motion.h2>
          </div>
          <motion.p variants={fadeInUp} className="text-stone-400 max-w-sm text-base leading-relaxed">
            Desde particulares hasta grandes empresas, ofrecemos soluciones adaptadas con la máxima calidad y puntualidad.
          </motion.p>
        </motion.div>

        {/* Main service cards */}
        <div className="grid lg:grid-cols-3 gap-5 mb-8">
          {services.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className="group relative rounded-3xl overflow-hidden cursor-pointer"
              onMouseEnter={() => setActive(i)}
            >
              <div className="relative h-[480px]">
                <img
                  src={s.image}
                  alt={s.title}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/50 to-transparent"></div>

                {/* Tag */}
                <div className="absolute top-5 left-5 w-10 h-10 flex items-center justify-center bg-teal-500/20 backdrop-blur-sm border border-teal-500/30 rounded-xl">
                  <span className="text-teal-300 text-xs font-bold">{s.tag}</span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 backdrop-blur-sm rounded-2xl mb-4 border border-teal-500/30">
                    <i className={`${s.icon} text-teal-300 text-xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-stone-300 text-sm leading-relaxed mb-4">{s.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {s.features.map((f, fi) => (
                      <span key={fi} className="px-3 py-1 bg-white/10 backdrop-blur-sm text-white/80 text-xs rounded-full border border-white/10">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Extra service pills */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {[
            { icon: 'ri-user-3-line', title: 'Particulares', desc: 'Cuidado experto para tu ropa personal' },
            { icon: 'ri-store-2-line', title: 'Spas & Bienestar', desc: 'Toallas y albornoces siempre frescos' },
            { icon: 'ri-shirt-line', title: 'Ropa Delicada', desc: 'Tratamientos especiales para prendas finas' },
            { icon: 'ri-briefcase-4-line', title: 'Oficinas', desc: 'Uniformes y textil corporativo' },
          ].map((item, i) => (
            <motion.div
              key={i}
              variants={scaleIn}
              whileHover={{ y: -4 }}
              className="bg-stone-900 border border-stone-800 hover:border-teal-700/50 rounded-2xl p-6 transition-all cursor-pointer"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-teal-500/10 rounded-xl mb-4">
                <i className={`${item.icon} text-teal-400 text-lg`}></i>
              </div>
              <h4 className="font-bold text-white mb-1">{item.title}</h4>
              <p className="text-sm text-stone-400">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── PROCESS ─── */
function ProcessSection() {
  const steps = [
    { num: '01', icon: 'ri-phone-line', title: 'Contacta con nosotros', desc: 'Llámanos o escríbenos para coordinar la recogida según tu horario.' },
    { num: '02', icon: 'ri-truck-line', title: 'Recogemos tu ropa', desc: 'Nuestro equipo pasa a recoger las prendas en el lugar que indiques.' },
    { num: '03', icon: 'ri-washing-machine-line', title: 'Lavado profesional', desc: 'Tratamos cada prenda con los productos y procesos adecuados.' },
    { num: '04', icon: 'ri-gift-line', title: 'Entrega impecable', desc: 'Devolvemos tu ropa limpia, planchada y perfectamente doblada en 48 horas.' },
  ];

  return (
    <section className="py-28 lg:py-36 bg-white overflow-hidden">
      <div className="w-full px-6 lg:px-14 xl:px-24">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center max-w-2xl mx-auto mb-20"
        >
          <motion.div variants={fadeInUp} className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-10 bg-teal-500"></div>
            <span className="text-teal-600 text-xs font-bold tracking-widest uppercase">Cómo Funciona</span>
            <div className="h-px w-10 bg-teal-500"></div>
          </motion.div>
          <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-black text-stone-900 leading-tight">
            Simple, rápido y sin complicaciones
          </motion.h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* Connector line */}
          <div className="hidden lg:block absolute top-14 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-teal-200 to-transparent"></div>

          {steps.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className="relative text-center"
            >
              <div className="relative inline-flex items-center justify-center w-28 h-28 mb-6">
                <div className="absolute inset-0 bg-teal-50 rounded-full"></div>
                <div className="absolute inset-3 bg-teal-100 rounded-full"></div>
                <div className="relative w-16 h-16 flex items-center justify-center bg-teal-600 rounded-full shadow-lg shadow-teal-600/30">
                  <i className={`${s.icon} text-white text-2xl`}></i>
                </div>
                <span className="absolute -top-1 -right-1 w-7 h-7 flex items-center justify-center bg-stone-900 text-white text-xs font-black rounded-full">
                  {s.num}
                </span>
              </div>
              <h4 className="text-lg font-bold text-stone-900 mb-2">{s.title}</h4>
              <p className="text-sm text-stone-500 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── DELIVERY ─── */
function DeliverySection() {
  const { ref, controls } = useScrollAnimation();

  return (
    <section id="reparto" className="py-28 lg:py-36 bg-stone-50 overflow-hidden">
      <div className="w-full px-6 lg:px-14 xl:px-24">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image side */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -60 }}
            animate={controls}
            variants={{ visible: { opacity: 1, x: 0, transition: { duration: 0.8 } } }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Barcelona%20aerial%20view%20with%20Sagrada%20Familia%20and%20Mediterranean%20sea%2C%20golden%20sunset%20light%2C%20vibrant%20city%20panorama%2C%20iconic%20architecture%2C%20warm%20orange%20and%20golden%20tones%2C%20professional%20drone%20photography%2C%20cinematic%20quality%2C%20beautiful%20cityscape%2C%20rich%20colors%2C%20stunning%20urban%20landscape&width=700&height=550&seq=del01&orientation=landscape"
                alt="Cobertura Barcelona"
                className="w-full h-[420px] lg:h-[520px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-teal-900/30 to-transparent"></div>
            </div>

            {/* Floating card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="absolute -bottom-8 -right-6 bg-white rounded-2xl p-6 shadow-2xl border border-stone-100"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-50 rounded-2xl">
                  <i className="ri-map-2-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <p className="text-3xl font-black text-stone-900">100%</p>
                  <p className="text-sm text-stone-500">Barcelona cubierta</p>
                </div>
              </div>
            </motion.div>

            {/* Second floating card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="absolute -top-6 -left-6 bg-teal-600 rounded-2xl p-5 shadow-xl text-white"
            >
              <i className="ri-truck-line text-2xl mb-1 block"></i>
              <p className="font-bold text-sm">Reparto Gratis</p>
              <p className="text-teal-200 text-xs">Sin coste adicional</p>
            </motion.div>
          </motion.div>

          {/* Content side */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-teal-500"></div>
              <span className="text-teal-600 text-xs font-bold tracking-widest uppercase">Servicio de Reparto</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-black text-stone-900 leading-tight mb-6">
              Recogida y entrega en toda{' '}
              <span className="text-teal-600">Barcelona</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-stone-500 text-base leading-relaxed mb-10">
              Nuestro servicio de reparto cubre toda la ciudad de Barcelona y zonas cercanas.
              Coordinamos directamente contigo para asegurar la máxima comodidad y puntualidad.
            </motion.p>

            <motion.div variants={fadeInUp} className="space-y-4 mb-10">
              {[
                { icon: 'ri-truck-line', title: 'Reparto Gratuito', desc: 'Sin coste adicional en toda Barcelona' },
                { icon: 'ri-time-line', title: 'Horario Flexible', desc: 'Adaptamos las recogidas a tu agenda' },
                { icon: 'ri-customer-service-2-line', title: 'Contacto Directo', desc: 'Comunicación constante con nuestro equipo' },
                { icon: 'ri-shield-check-line', title: 'Seguro Incluido', desc: 'Tus prendas siempre protegidas' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeInUp}
                  className="flex items-center gap-4 p-4 rounded-2xl hover:bg-white transition-all border border-transparent hover:border-stone-100 hover:shadow-sm"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-teal-600 text-xl`}></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-stone-900">{item.title}</h4>
                    <p className="text-sm text-stone-500">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div variants={fadeInUp}>
              <button
                onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-stone-900 text-white font-bold rounded-full hover:bg-teal-600 transition-all shadow-lg cursor-pointer whitespace-nowrap"
              >
                Consultar Cobertura en tu Zona
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── TEAM / EQUIPMENT ─── */
function TeamSection() {
  const { ref, controls } = useScrollAnimation();

  return (
    <section id="equipo" className="py-28 lg:py-36 bg-white overflow-hidden">
      <div className="w-full px-6 lg:px-14 xl:px-24">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Content */}
          <motion.div ref={ref} initial="hidden" animate={controls} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-teal-500"></div>
              <span className="text-teal-600 text-xs font-bold tracking-widest uppercase">Tecnología de Vanguardia</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-black text-stone-900 leading-tight mb-6">
              Equipamiento profesional para{' '}
              <span className="text-teal-600">resultados impecables</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-stone-500 text-base leading-relaxed mb-10">
              Contamos con maquinaria industrial de última generación y productos especializados
              que garantizan el mejor cuidado para cada tipo de prenda y tejido.
            </motion.p>

            <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4 mb-8">
              {[
                { icon: 'ri-washing-machine-line', title: 'Lavadoras Industriales' },
                { icon: 'ri-temp-hot-line', title: 'Secadoras Profesionales' },
                { icon: 'ri-iron-line', title: 'Planchas Industriales' },
                { icon: 'ri-windy-line', title: 'Máquinas de Secado' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.03 }}
                  className="bg-stone-50 border border-stone-100 rounded-2xl p-5 cursor-pointer hover:border-teal-200 transition-all"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl mb-3">
                    <i className={`${item.icon} text-teal-600 text-xl`}></i>
                  </div>
                  <p className="text-sm font-bold text-stone-900">{item.title}</p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-2">
              {['Ecológicos', 'Hipoalergénicos', 'Desinfectantes', 'Suavizantes Premium'].map((tag, i) => (
                <span key={i} className="px-4 py-2 bg-teal-50 text-teal-700 text-sm font-medium rounded-full border border-teal-100">
                  {tag}
                </span>
              ))}
            </motion.div>
          </motion.div>

          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="rounded-3xl overflow-hidden shadow-xl"
              >
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20industrial%20laundry%20facility%20with%20stainless%20steel%20washing%20machines%2C%20clean%20organized%20workspace%2C%20professional%20equipment%2C%20bright%20lighting%2C%20high%20tech%20textile%20machinery%2C%20commercial%20laundry%20interior%2C%20spotless%20environment%2C%20industrial%20photography%2C%20warm%20tones&width=400&height=520&seq=team01&orientation=portrait"
                  alt="Equipamiento profesional"
                  className="w-full h-[300px] object-cover object-top"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="rounded-3xl overflow-hidden shadow-xl mt-10"
              >
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20laundry%20worker%20carefully%20folding%20pristine%20white%20towels%2C%20industrial%20laundry%20setting%2C%20expert%20textile%20care%2C%20warm%20studio%20lighting%2C%20quality%20service%2C%20attention%20to%20detail%2C%20clean%20uniform%2C%20professional%20atmosphere%2C%20warm%20tones%2C%20skilled%20worker&width=400&height=520&seq=team02&orientation=portrait"
                  alt="Equipo profesional"
                  className="w-full h-[300px] object-cover object-top"
                />
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-white rounded-2xl p-5 shadow-2xl border border-stone-100"
            >
              <div className="flex items-center gap-3">
                <ColumbusLogo className="w-12 h-12" />
                <div>
                  <p className="font-black text-stone-900 text-sm">Lavandería Colón</p>
                  <p className="text-xs text-teal-600 font-medium">Barcelona · Desde 1994</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── CONTACT ─── */
function ContactSection() {
  const { ref, controls } = useScrollAnimation();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({ nombre: '', email: '', telefono: '', tipo: '', mensaje: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.mensaje.length > 500) return;
    setFormStatus('submitting');
    try {
      const body = new URLSearchParams({
        nombre: formData.nombre,
        email: formData.email,
        telefono: formData.telefono,
        tipo: formData.tipo,
        mensaje: formData.mensaje,
      }).toString();
      const res = await fetch('https://readdy.ai/api/form/d6mqvep4a3uojp5fgsjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (res.ok) {
        setFormStatus('success');
        setFormData({ nombre: '', email: '', telefono: '', tipo: '', mensaje: '' });
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 4000);
      }
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 4000);
    }
  };

  return (
    <section id="contacto" className="py-28 lg:py-36 bg-white overflow-hidden">
      <div className="w-full px-6 lg:px-14 xl:px-24">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
          className="max-w-6xl mx-auto"
        >
          <motion.div variants={fadeInUp} className="text-center max-w-2xl mx-auto mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-teal-500"></div>
              <span className="text-teal-600 text-xs font-bold tracking-widest uppercase">Contacto</span>
              <div className="h-px w-10 bg-teal-500"></div>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-stone-900 leading-tight mb-4">
              Solicita tu presupuesto
            </h2>
            <p className="text-stone-500">Respuesta en menos de 24 horas. Sin compromiso.</p>
          </motion.div>

          {/* Info cards */}
          <motion.div variants={fadeInUp} className="grid md:grid-cols-3 gap-5 mb-10">
            <div className="bg-stone-50 border border-stone-100 rounded-3xl p-7">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-50 rounded-2xl mb-5">
                <i className="ri-phone-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Teléfono</h3>
              <p className="text-sm text-stone-500 mb-4">Llámanos para consultas urgentes</p>
              <a href="tel:620550559" className="block text-teal-600 font-bold hover:text-teal-700 cursor-pointer">620 550 559</a>
              <a href="tel:933020486" className="block text-teal-600 font-bold hover:text-teal-700 cursor-pointer">933 020 486</a>
            </div>

            <div className="bg-stone-50 border border-stone-100 rounded-3xl p-7">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-50 rounded-2xl mb-5">
                <i className="ri-mail-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Email</h3>
              <p className="text-sm text-stone-500 mb-4">Escríbenos para presupuestos</p>
              <a href="mailto:lavanderiaacolon@gmail.com" className="text-teal-600 font-bold hover:text-teal-700 text-sm cursor-pointer">
                lavanderiaacolon@gmail.com
              </a>
            </div>

            <div className="bg-stone-900 rounded-3xl p-7 text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-500/20 rounded-2xl mb-5">
                <i className="ri-map-pin-line text-teal-300 text-2xl"></i>
              </div>
              <h3 className="font-bold mb-2">Ubicación</h3>
              <p className="text-stone-400 text-sm mb-4">Visítanos en nuestro local</p>
              <p className="font-bold">Calle Ample 9</p>
              <p className="text-stone-400 text-sm">08002 Barcelona · Barrio Gótico</p>
            </div>
          </motion.div>

          {/* Form */}
          <motion.form
            variants={fadeInUp}
            onSubmit={handleSubmit}
            data-readdy-form
            id="contact-form-colon"
            className="bg-stone-50 border border-stone-100 rounded-3xl p-8 lg:p-12"
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">Nombre completo</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                  placeholder="Tu nombre"
                  className="w-full px-4 py-3.5 rounded-xl border border-stone-200 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="tu@email.com"
                  className="w-full px-4 py-3.5 rounded-xl border border-stone-200 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">Teléfono</label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="620 550 559"
                  className="w-full px-4 py-3.5 rounded-xl border border-stone-200 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">Tipo de cliente</label>
                <select
                  name="tipo"
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  required
                  className="w-full px-4 py-3.5 rounded-xl border border-stone-200 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                >
                  <option value="">Selecciona...</option>
                  <option value="particular">Particular</option>
                  <option value="hotel">Hotel</option>
                  <option value="pension">Pensión</option>
                  <option value="hostal">Hostal</option>
                  <option value="restaurante">Restaurante</option>
                  <option value="apartamento">Apartamento Turístico</option>
                  <option value="medico">Centro Médico</option>
                  <option value="spa">Spa / Bienestar</option>
                  <option value="empresa">Otra Empresa</option>
                </select>
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-stone-700 mb-2">Mensaje</label>
              <textarea
                name="mensaje"
                value={formData.mensaje}
                onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                rows={4}
                maxLength={500}
                placeholder="Cuéntanos qué necesitas..."
                className="w-full px-4 py-3.5 rounded-xl border border-stone-200 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all resize-none text-sm"
              ></textarea>
              <p className="text-xs text-stone-400 mt-1">{formData.mensaje.length}/500 caracteres</p>
            </div>

            {formStatus === 'success' && (
              <div className="mb-5 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-lg"></i>
                ¡Mensaje enviado! Te responderemos en menos de 24 horas.
              </div>
            )}
            {formStatus === 'error' && (
              <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                <i className="ri-error-warning-line text-lg"></i>
                Ha ocurrido un error. Por favor, inténtalo de nuevo o contáctanos por teléfono.
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-stone-400">Protegemos tus datos según la LOPD</p>
              <button
                type="submit"
                disabled={formStatus === 'submitting' || formStatus === 'success'}
                className={`px-8 py-4 rounded-full font-bold transition-all whitespace-nowrap cursor-pointer ${
                  formStatus === 'success'
                    ? 'bg-emerald-500 text-white'
                    : formStatus === 'submitting'
                    ? 'bg-stone-400 text-white cursor-not-allowed'
                    : 'bg-stone-900 text-white hover:bg-teal-600'
                }`}
              >
                {formStatus === 'submitting' && <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin"></i> Enviando...</span>}
                {formStatus === 'success' && <span className="flex items-center gap-2"><i className="ri-checkbox-circle-line"></i> ¡Enviado!</span>}
                {formStatus === 'idle' && 'Enviar Consulta'}
                {formStatus === 'error' && 'Reintentar'}
              </button>
            </div>
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── FOOTER ─── */
function Footer() {
  const go = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer className="bg-stone-100 border-t border-stone-200 text-stone-700 py-16 lg:py-20">
      <div className="w-full px-6 lg:px-14 xl:px-24">
        <div className="grid lg:grid-cols-4 gap-12 mb-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <ColumbusLogo className="w-9 h-9" />
              <span className="text-lg font-black text-stone-900">
                Lavandería<span className="text-teal-600">Colón</span>
              </span>
            </div>
            <p className="text-stone-500 text-sm leading-relaxed mb-6">
              Limpieza Profesional.<br />Confianza Barcelonesa.
            </p>
            <div className="flex gap-3">
              {[
                { icon: 'ri-instagram-line', href: '#' },
                { icon: 'ri-facebook-line', href: '#' },
                { icon: 'ri-whatsapp-line', href: 'https://wa.me/34620550559' },
              ].map((s, i) => (
                <a key={i} href={s.href} className="w-10 h-10 flex items-center justify-center bg-stone-200 hover:bg-teal-600 hover:text-white rounded-xl transition-all cursor-pointer">
                  <i className={s.icon}></i>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-bold text-stone-900 mb-4">Servicios</h4>
            <ul className="space-y-3 text-sm text-stone-500">
              {['Particulares', 'Hoteles', 'Restaurantes', 'Spas'].map((s, i) => (
                <li key={i}><button onClick={() => go('servicios')} className="hover:text-teal-600 transition-colors cursor-pointer">{s}</button></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-stone-900 mb-4">Empresa</h4>
            <ul className="space-y-3 text-sm text-stone-500">
              <li><button onClick={() => go('equipo')} className="hover:text-teal-600 transition-colors cursor-pointer">Sobre Nosotros</button></li>
              <li><button onClick={() => go('reparto')} className="hover:text-teal-600 transition-colors cursor-pointer">Cobertura</button></li>
              <li><button onClick={() => go('contacto')} className="hover:text-teal-600 transition-colors cursor-pointer">Contacto</button></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-stone-900 mb-4">Contacto</h4>
            <ul className="space-y-3 text-sm text-stone-500">
              <li className="flex items-center gap-2"><i className="ri-map-pin-line text-teal-600"></i> Calle Ample 9, Barcelona 08002</li>
              <li className="flex items-center gap-2"><i className="ri-phone-line text-teal-600"></i><a href="tel:620550559" className="hover:text-teal-600 cursor-pointer">620 550 559</a></li>
              <li className="flex items-center gap-2"><i className="ri-phone-line text-teal-600"></i><a href="tel:933020486" className="hover:text-teal-600 cursor-pointer">933 020 486</a></li>
              <li className="flex items-center gap-2"><i className="ri-mail-line text-teal-600"></i><a href="mailto:lavanderiaacolon@gmail.com" className="hover:text-teal-600 cursor-pointer text-xs">lavanderiaacolon@gmail.com</a></li>
              <li className="flex items-start gap-2 pt-2 border-t border-stone-200">
                <i className="ri-time-line text-teal-600 mt-0.5"></i>
                <div>
                  <p className="font-semibold text-stone-700 mb-1">Horario</p>
                  <p>Lun – Vie: 9:00 – 14:00 / 16:00 – 18:00</p>
                  <p>Sábado: Cerrado</p>
                  <p>Domingo: Cerrado</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-stone-200 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-stone-400">© 2025 Lavandería Colón Barcelona. Todos los derechos reservados.</p>
          <div className="flex gap-6 text-sm text-stone-400">
            <a href="#" className="hover:text-teal-600 transition-colors cursor-pointer">Política de Privacidad</a>
            <a href="#" className="hover:text-teal-600 transition-colors cursor-pointer">Términos de Servicio</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── MAIN ─── */
export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      <SeoJsonLd />
      <Navbar />
      <main>
        <HeroSection />
        <ServicesSection />
        <ProcessSection />
        <DeliverySection />
        <TeamSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}
